import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plant, ArrowRight, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { IndiaMandiMark } from "@/components/Logo";
import { formatErr } from "@/lib/api";

const AUTH_BG = "https://images.unsplash.com/photo-1558388556-2261d4cc1938?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      nav("/app");
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (role) => {
    setEmail(`${role}@indiamandi.in`);
    setPassword(role === "admin" ? "Admin@123" : "Test@123");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50" data-testid="login-page">
      {/* Left form */}
      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <IndiaMandiMark size={38} />
          </Link>
          <LanguageToggle />
        </div>
        <div className="flex-1 flex items-center">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={submit}
            className="w-full max-w-md mx-auto"
          >
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Login</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-emerald-950 mt-2">{t("auth.login_title")}</h1>
            <p className="text-stone-600 mt-2">{t("auth.login_sub")}</p>

            <div className="mt-8 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("auth.email")}</label>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100 outline-none bg-white"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("auth.password")}</label>
                <input
                  type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full px-4 py-3 rounded-md border border-stone-300 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100 outline-none bg-white"
                  data-testid="login-password-input"
                />
              </div>
              {err && (
                <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                  <Warning weight="fill" size={16} /> {err}
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-bold transition-colors"
                data-testid="login-submit-btn"
              >
                {busy ? "Logging in…" : t("auth.login_btn")} <ArrowRight size={18} weight="bold" />
              </button>
            </div>

            {process.env.REACT_APP_SHOW_DEMO === "1" && (
            <div className="mt-6">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">Demo Accounts</div>
              <div className="grid grid-cols-3 gap-2">
                {["admin", "farmer", "trader", "mandi", "wholesaler", "exporter"].map((r) => (
                  <button
                    key={r} type="button" onClick={() => fillDemo(r)}
                    className="text-xs px-2 py-1.5 rounded-md border border-stone-200 hover:border-emerald-700 hover:text-emerald-900 bg-white text-stone-700 capitalize font-semibold transition-colors"
                    data-testid={`demo-${r}-btn`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            )}

            <p className="mt-8 text-sm text-stone-600">
              {t("auth.no_account")} <Link to="/register" className="font-bold text-emerald-900 hover:text-orange-600" data-testid="link-register">{t("auth.register_btn")}</Link>
            </p>
          </motion.form>
        </div>
        <div className="text-xs text-stone-400 font-mono">© GLC Zone Pvt. Ltd. · mandi.glczone.in</div>
      </div>

      {/* Right image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={AUTH_BG} alt="India agriculture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-950 via-emerald-950/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="max-w-lg">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400 mb-4">Trusted by farmers across Bharat</div>
            <p className="font-display text-3xl leading-snug font-bold">
              "In three months, our wheat sold at 6.2% higher rates than the local mandi — and we got paid the same day it was delivered."
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-orange-500 grid place-items-center font-black">RK</div>
              <div>
                <div className="font-semibold">Ramesh Kumar</div>
                <div className="text-emerald-200 text-sm">Farmer, Ludhiana</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
